import React, { useState, useRef } from "react";
import {
  Box, Typography, Tabs, Tab, Paper, Button, Alert, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import { importCsv, downloadExport, downloadTemplate } from "../services/api";
import toast, { Toaster } from "react-hot-toast";

const TABS = [
  { key: "products",     label: "Ürünler",      desc: "id, sku, barcode, name, description, category_id, price, stock, unit, tax_rate, bizimhesap_id, is_active" },
  { key: "customers",    label: "Müşteriler",   desc: "name, email, phone, company_name, tax_no, tc_no, city, district, address, customer_group_id" },
  { key: "price-tiers",  label: "Fiyat Tier",   desc: "product_id, group_id, min_qty, price, valid_from, valid_until" }
];

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
}

export default function Import() {
  const [tab, setTab] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirm, setConfirm] = useState(null); // { file, lineCount }
  const [drag, setDrag] = useState(false);
  const [orderRange, setOrderRange] = useState({ from: "", to: "" });
  const fileInputRef = useRef(null);

  const current = TABS[tab];

  const reset = () => { setResult(null); };

  const onTab = (_, v) => { setTab(v); reset(); };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      return toast.error("Sadece .csv yüklenebilir");
    }
    if (file.size > 5 * 1024 * 1024) {
      return toast.error("Dosya 5 MB sınırını aşıyor");
    }

    // Büyük dosya için onay (1000+ satır)
    const lineCount = await new Promise(resolve => {
      const r = new FileReader();
      r.onload = e => resolve((e.target.result.match(/\n/g) || []).length);
      r.readAsText(file.slice(0, Math.min(file.size, 1024 * 1024)));
    });
    if (lineCount > 1000) {
      setConfirm({ file, lineCount });
      return;
    }
    runImport(file);
  };

  const runImport = async (file) => {
    setBusy(true); setResult(null); setConfirm(null);
    try {
      const r = await importCsv(current.key, file);
      setResult(r.data);
      const { imported = 0, updated = 0, errors = [] } = r.data;
      if (errors.length === 0) {
        toast.success(`${imported} eklendi · ${updated} güncellendi`);
      } else {
        toast.error(`${errors.length} satır hatalı (${imported}+${updated} başarılı)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "Import başarısız");
    } finally { setBusy(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDrag(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleExport = async () => {
    setBusy(true);
    try {
      const blob = await downloadExport(current.key);
      downloadBlob(blob, `${current.key}_${new Date().toISOString().slice(0,10)}.csv`);
      toast.success("Export indirildi");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Export başarısız");
    } finally { setBusy(false); }
  };

  const handleExportOrders = async () => {
    if (!orderRange.from || !orderRange.to) return toast.error("Tarih aralığı seçin");
    setBusy(true);
    try {
      const blob = await downloadExport("orders", `from=${orderRange.from}&to=${orderRange.to}`);
      downloadBlob(blob, `orders_${orderRange.from}_${orderRange.to}.csv`);
      toast.success("Sipariş export indirildi");
    } catch (e) { toast.error("Export başarısız"); }
    finally { setBusy(false); }
  };

  const handleTemplate = async () => {
    try {
      const blob = await downloadTemplate(current.key);
      downloadBlob(blob, `${current.key}_template.csv`);
    } catch { toast.error("Şablon indirilemedi"); }
  };

  const downloadErrorsCsv = () => {
    if (!result?.errors?.length) return;
    const rows = ["satir,hata"].concat(
      result.errors.map(e => `${e.row},"${(e.message || "").replace(/"/g, '""')}"`)
    );
    downloadBlob(new Blob(["﻿" + rows.join("\n")], { type: "text/csv;charset=utf-8" }), `${current.key}_hatalar.csv`);
  };

  return (
    <Box>
      <Toaster />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>📤 İçe / Dışa Aktar</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<DescriptionIcon />} variant="outlined" onClick={handleTemplate}>Şablon İndir</Button>
          <Button startIcon={<DownloadIcon />} variant="contained" onClick={handleExport} disabled={busy} sx={{ bgcolor: "#1565C0" }}>
            CSV Export
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={onTab}>
          {TABS.map(t => <Tab key={t.key} label={t.label} />)}
        </Tabs>
      </Paper>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Beklenen sütunlar:</strong> {current.desc}
        <br />
        <strong>Upsert anahtarı:</strong>{" "}
        {current.key === "products" && "id → sku → barcode → bizimhesap_id (sırasıyla)"}
        {current.key === "customers" && "email"}
        {current.key === "price-tiers" && "product_id + group_id + min_qty"}
      </Alert>

      {/* Drag-drop alanı */}
      <Paper
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={handleDrop}
        sx={{
          p: 4, mb: 2, textAlign: "center", cursor: "pointer",
          border: "2px dashed " + (drag ? "#1565C0" : "#ccc"),
          bgcolor: drag ? "#E3F2FD" : "transparent",
          transition: "all 0.15s"
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <UploadFileIcon sx={{ fontSize: 48, color: "#1565C0", mb: 1 }} />
        <Typography variant="h6">CSV dosyasını sürükleyip bırakın</Typography>
        <Typography variant="caption" color="text.secondary">
          veya tıklayıp seçin · max 5 MB · max 10.000 satır
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".csv,text/csv"
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; handleFile(f); }}
        />
      </Paper>

      {busy && <LinearProgress sx={{ mb: 2 }} />}

      {/* Sipariş tarih aralığı (sadece price-tiers tabında değilken görünür değil) */}
      {tab === 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" mb={1}>📦 Sipariş Export (tarih aralığı)</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField type="date" label="Başlangıç" size="small" InputLabelProps={{ shrink: true }}
              value={orderRange.from} onChange={e => setOrderRange({ ...orderRange, from: e.target.value })} />
            <TextField type="date" label="Bitiş" size="small" InputLabelProps={{ shrink: true }}
              value={orderRange.to} onChange={e => setOrderRange({ ...orderRange, to: e.target.value })} />
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportOrders} disabled={busy}>
              Sipariş Export
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Sonuç */}
      {result && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
            <Chip label={`Toplam: ${result.total}`} />
            <Chip label={`Eklendi: ${result.imported}`} color="success" />
            <Chip label={`Güncellendi: ${result.updated}`} color="primary" />
            <Chip label={`Atlandı: ${result.skipped || 0}`} color="warning" />
            <Chip label={`Hata: ${result.errors?.length || 0}`} color={(result.errors?.length || 0) > 0 ? "error" : "default"} />
            {result.errors?.length > 0 && (
              <Button size="small" startIcon={<DownloadIcon />} onClick={downloadErrorsCsv}>
                Hatalı Satırları İndir
              </Button>
            )}
          </Stack>

          {result.errors?.length > 0 && (
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#FEF2F2", fontWeight: 700, width: 80 }}>Satır</TableCell>
                    <TableCell sx={{ bgcolor: "#FEF2F2", fontWeight: 700 }}>Hata</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.errors.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ color: "#B91C1C", fontWeight: 700 }}>#{e.row}</TableCell>
                      <TableCell>{e.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Büyük dosya onay diyaloğu */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>Toplu içe aktarım</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{confirm?.file?.name}</strong> dosyasında <strong>{confirm?.lineCount}+</strong> satır var.
            <br />Devam edilsin mi? (max 10.000 satır)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>İptal</Button>
          <Button variant="contained" sx={{ bgcolor: "#1565C0" }} onClick={() => runImport(confirm.file)}>Evet, İçe Aktar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
